import { ticketSearchFilter } from '../shared/utils/ticketSearchFilter';
import { ticketSortParam } from '../tikit/List/ticketSortParam';

// The user page's Tickets tab asks the same Ticket endpoint the ticket list does, so the server applies
// the signed-in agent's scoping exactly as it does there. Its search is the ticket list's filter text box -
// Id, request title and comments - through the same builder.

export interface UserTicketsQueryOptions {
  userId: number;
  page: number;
  pageSize: number;
  orderByField: string;
  descending: boolean;
  /** Text from the tab's search box; empty for none. */
  search?: string;
  /** Ticket Tags is licensed, so the Tags column shows and its tags are fetched. */
  tags?: boolean;
  /** Ticket Templates is licensed, so the Template column shows and each ticket's template name is fetched. */
  template?: boolean;
  /** Resolution notes are on, so the resolution columns show and each ticket's resolution is fetched. */
  resolution?: boolean;
  /** Group Chat is licensed, so the Group Chat column shows and each ticket's chat and members are fetched. */
  groupChat?: boolean;
}

// The list's own expand, plus the team and group names, which the list reads from app state and the user page
// never loads. Resolution and GroupChat are asked for only when their columns can show.
const EXPAND = 'AffectedUsers($select=PlatformUserId),TicketCollaborators($select=PlatformUserId),TicketLifecycle($expand=Status,Lifecycle($select=Title),Phases($filter=IsCurrent eq true)),Team($select=FriendlyName),SupportGroup($select=Name)';

const GROUP_CHAT_EXPAND = 'GroupChat($select=ConversationId,Id;$expand=Members($select=Id,AadObjectId,UserName,FullName))';

export const userTicketsFilter = (userId: number) =>
  `(RequesterId eq ${userId} or AffectedUsers/any(a: a/PlatformUserId eq ${userId}))`;

// URL-encoded as the ticket list does, after doubling any quote - OData's escape for one inside a string,
// which the list leaves out, so a search for "Adele's" breaks the request there.
export const userTicketsSearch = (search: string) =>
  ticketSearchFilter(encodeURIComponent(search.replace(/'/g, "''")), { matchId: true, comments: 'all' });

export const buildUserTicketsQuery = ({ userId, page, pageSize, orderByField, descending, search, tags, template, resolution, groupChat }: UserTicketsQueryOptions) => {
  const trimmed = search?.trim();
  const filter = trimmed ? `${userTicketsFilter(userId)} and ${userTicketsSearch(trimmed)}` : userTicketsFilter(userId);
  const groupChatExpand = groupChat ? ',' + GROUP_CHAT_EXPAND : '';
  return `?$expand=${EXPAND}${tags ? ',Tags($select=Id,Name)' : ''}${template ? ',Template($select=Id,Name)' : ''}` +
    `${resolution ? ',Resolution' : ''}${groupChatExpand}` +
    `&$filter=${filter}` +
    `&${ticketSortParam(orderByField, descending)}` +
    `&$top=${pageSize}&$skip=${(Math.max(page, 1) - 1) * pageSize}&$count=true`;
};
